// autoSelectToggle.js
import * as Cesium from "cesium";

const viewerStateMap = new WeakMap(); // per-viewer state

// tile size in degrees (approx 11m)
const TILE_SIZE_DEG = 0.0001;
const MAX_DISTANCE_M = 0.05; // 5 cm

function toDeg(rad) {
  return (rad * 180) / Math.PI;
}

function makeTileKey(latDeg, lonDeg) {
  const x = Math.floor(lonDeg / TILE_SIZE_DEG);
  const y = Math.floor(latDeg / TILE_SIZE_DEG);
  return `${x}:${y}`;
}

function horizontalDistanceMeters(lat1Deg, lon1Deg, lat2Deg, lon2Deg) {
  // height ignored by setting 0
  const c1 = Cesium.Cartographic.fromDegrees(lon1Deg, lat1Deg, 0);
  const c2 = Cesium.Cartographic.fromDegrees(lon2Deg, lat2Deg, 0);
  const geodesic = new Cesium.EllipsoidGeodesic(c1, c2);
  return geodesic.surfaceDistance;
}

function getPolylineEndpoints(entity) {
  // get positions at "now"
  const time = Cesium.JulianDate.now();
  if (!entity.polyline || !entity.polyline.positions) return null;

  const positions = entity.polyline.positions.getValue(time);
  if (!positions || positions.length < 2) return null;

  const firstCart = positions[0];
  const lastCart = positions[positions.length - 1];

  const firstCarto = Cesium.Cartographic.fromCartesian(firstCart);
  const lastCarto = Cesium.Cartographic.fromCartesian(lastCart);

  const first = {
    latDeg: toDeg(firstCarto.latitude),
    lonDeg: toDeg(firstCarto.longitude),
  };
  const last = {
    latDeg: toDeg(lastCarto.latitude),
    lonDeg: toDeg(lastCarto.longitude),
  };

  return {
    start: first,
    end: last,
  };
}

function buildEndpointIndex(dataSource) {
  // index endpoints in tiles
  const tiles = new Map(); // key -> EndpointRecord[]
  const entityEndpoints = new Map(); // entity -> { start, end }

  dataSource.entities.values.forEach((entity) => {
    const endpoints = getPolylineEndpoints(entity);
    if (!endpoints) return;

    const recordStart = {
      entity,
      kind: "start",
      latDeg: endpoints.start.latDeg,
      lonDeg: endpoints.start.lonDeg,
    };
    const recordEnd = {
      entity,
      kind: "end",
      latDeg: endpoints.end.latDeg,
      lonDeg: endpoints.end.lonDeg,
    };

    entityEndpoints.set(entity, {
      start: recordStart,
      end: recordEnd,
    });

    const keyStart = makeTileKey(recordStart.latDeg, recordStart.lonDeg);
    const keyEnd = makeTileKey(recordEnd.latDeg, recordEnd.lonDeg);

    if (!tiles.has(keyStart)) tiles.set(keyStart, []);
    tiles.get(keyStart).push(recordStart);

    if (!tiles.has(keyEnd)) tiles.set(keyEnd, []);
    tiles.get(keyEnd).push(recordEnd);
  });

  return { tiles, entityEndpoints };
}

function findNearestEndpoint(index, targetLatDeg, targetLonDeg, options) {
  // options: { excludeEntities: Set<Entity>, maxDistanceMeters }
  const { tiles } = index;
  const maxDistance = options.maxDistanceMeters ?? MAX_DISTANCE_M;
  const excludeEntities = options.excludeEntities ?? new Set();

  const lonTile = Math.floor(targetLonDeg / TILE_SIZE_DEG);
  const latTile = Math.floor(targetLatDeg / TILE_SIZE_DEG);

  let bestRecord = null;
  let bestDistance = Number.POSITIVE_INFINITY;

  for (let dx = -1; dx <= 1; dx += 1) {
    for (let dy = -1; dy <= 1; dy += 1) {
      const key = `${lonTile + dx}:${latTile + dy}`;
      const list = tiles.get(key);
      if (!list) continue;

      for (const rec of list) {
        if (excludeEntities.has(rec.entity)) continue;

        const d = horizontalDistanceMeters(targetLatDeg, targetLonDeg, rec.latDeg, rec.lonDeg);

        if (d <= maxDistance && d < bestDistance) {
          bestDistance = d;
          bestRecord = rec;
        }
      }
    }
  }

  return bestRecord;
}

function saveStyleAndColorRed(entity, styleBackupMap) {
  if (!entity.polyline && !entity.polygon) return;

  ensureOriginalStyle(entity); // save original style once

  if (!styleBackupMap.has(entity)) {
    styleBackupMap.set(entity, {
      polylineMaterial: entity.polyline ? entity.polyline.material : undefined,
    });
  }

  if (entity.polygon) {
    entity.polygon.material = Cesium.Color.RED;
  }

  if (entity.polyline) {
    entity.polyline.material = Cesium.Color.RED;
  }
}

function saveStyleAndColorBlue(entity, styleBackupMap) {
  if (!entity.polyline && !entity.polygon) return;

  ensureOriginalStyle(entity); // save original style once

  if (!styleBackupMap.has(entity)) {
    styleBackupMap.set(entity, {
      polylineMaterial: entity.polyline ? entity.polyline.material : undefined,
    });
  }

  entity.polyline.material = Cesium.Color.BLUE;
}

function restoreStyle(entity, styleBackupMap) {
  const backup = styleBackupMap.get(entity);
  if (!backup) return;
  if (entity.polyline) {
    entity.polyline.material = backup.polylineMaterial;
  }
}

async function followChainFromFirstEntity(state, firstEntity) {
  const { selectedEntities, entityEndpoints, styleBackupMap } = state;

  // first entity is always accepted, color red
  selectedEntities.add(firstEntity);
  saveStyleAndColorRed(firstEntity, styleBackupMap);

  if (state.ui && typeof state.ui.onEntityAccepted === "function") {
    state.ui.onEntityAccepted(firstEntity, Array.from(selectedEntities));
  }

  const endpoints = entityEndpoints.get(firstEntity);
  if (!endpoints) return;

  let currentEntity = firstEntity;
  let outgoingEndpoint = endpoints.end;

  while (true) {
    const exclude = new Set(selectedEntities);
    exclude.add(currentEntity);

    const nextEndpoint = findNearestEndpoint(state.index, outgoingEndpoint.latDeg, outgoingEndpoint.lonDeg, {
      excludeEntities: exclude,
      maxDistanceMeters: MAX_DISTANCE_M,
    });

    if (!nextEndpoint) {
      if (state.ui && typeof state.ui.onNoNeighbor === "function") {
        state.ui.onNoNeighbor({
          fromEntity: currentEntity,
          outgoingEndpoint,
        });
      }
      break;
    }

    const candidateEntity = nextEndpoint.entity;

    // candidate before confirm -> blue
    saveStyleAndColorBlue(candidateEntity, styleBackupMap);

    let ok = true;

    if (state.ui && typeof state.ui.confirmNext === "function") {
      ok = await state.ui.confirmNext({
        candidateEntity,
        fromEntity: currentEntity,
        targetEndpoint: outgoingEndpoint,
        nextEndpoint,
      });
    } else {
      ok = window.confirm("Use this entity as next in chain?");
    }

    if (!ok) {
      restoreStyle(candidateEntity, styleBackupMap);
      break;
    }

    // confirmed by user
    selectedEntities.add(candidateEntity);

    // now final color -> red
    if (candidateEntity.polyline) {
      candidateEntity.polyline.material = Cesium.Color.RED;
    }

    if (state.ui && typeof state.ui.onEntityAccepted === "function") {
      state.ui.onEntityAccepted(candidateEntity, Array.from(selectedEntities));
    }

    currentEntity = candidateEntity;
    const candidateEndpoints = entityEndpoints.get(candidateEntity);
    if (!candidateEndpoints) break;

    if (nextEndpoint.kind === "start") {
      outgoingEndpoint = candidateEndpoints.end;
    } else {
      outgoingEndpoint = candidateEndpoints.start;
    }
  }
}

function startAutoSelect(viewer, uiCallbacks = {}) {
  const state = {
    active: true,
    viewer,
    handler: null,
    dataSource: null,
    index: null,
    entityEndpoints: null,
    selectedEntities: new Set(),
    styleBackupMap: new Map(),
    ui: {
      confirmNext: uiCallbacks.confirmNext,
      onNoNeighbor: uiCallbacks.onNoNeighbor,
      onEntityAccepted: uiCallbacks.onEntityAccepted, // new
    },
  };

  const handler = new Cesium.ScreenSpaceEventHandler(viewer.scene.canvas);
  state.handler = handler;

  handler.setInputAction(async (movement) => {
    const picked = viewer.scene.pick(movement.position);
    if (!Cesium.defined(picked) || !picked.id) return;

    const entity = picked.id;

    // if this is the first click
    if (!state.dataSource) {
      if (!entity.entityCollection || !entity.entityCollection.owner) {
        return;
      }

      state.dataSource = entity.entityCollection.owner;

      // build index for this dataSource
      const { tiles, entityEndpoints } = buildEndpointIndex(state.dataSource);
      state.index = { tiles };
      state.entityEndpoints = entityEndpoints;
    }

    // only accept entities from same dataSource
    const owner = entity.entityCollection && entity.entityCollection.owner;
    if (owner !== state.dataSource) return;

    // if entity has no endpoints in index, ignore
    if (!state.entityEndpoints || !state.entityEndpoints.get(entity)) {
      return;
    }

    // run chain from this entity
    await followChainFromFirstEntity(state, entity);
  }, Cesium.ScreenSpaceEventType.LEFT_CLICK);

  return state;
}

function stopAutoSelect(state) {
  if (state.handler) {
    state.handler.destroy();
    state.handler = null;
  }

  // do NOT restore styles here anymore
  state.styleBackupMap.clear();
}

function ensureOriginalStyle(entity) {
  if (entity._originalStyle) return; // already saved

  entity._originalStyle = {
    polygonMaterial: entity.polygon ? entity.polygon.material : undefined,
    polylineMaterial: entity.polyline ? entity.polyline.material : undefined,
    polylineWidth: entity.polyline ? entity.polyline.width : undefined,
  };
}

export function toggleAutoSelect(viewer, uiCallbacks = {}) {
  if (!viewer) {
    return { active: false, finished: false };
  }

  const existing = viewerStateMap.get(viewer);

  if (!existing || !existing.active) {
    const state = startAutoSelect(viewer, uiCallbacks);
    viewerStateMap.set(viewer, state);
    return { active: true, finished: false };
  }

  // stop and gather result
  stopAutoSelect(existing);
  viewerStateMap.delete(viewer);

  const entities = Array.from(existing.selectedEntities);
  return { active: false, finished: true, entities };
}

export function removeSelectedEntity(viewer, entity) {
  if (!viewer || !entity) return;

  const state = viewerStateMap.get(viewer);
  if (!state || !state.active) return;

  // remove from selected set
  if (state.selectedEntities.has(entity)) {
    state.selectedEntities.delete(entity);
  }

  // restore style if we changed it
  restoreStyle(entity, state.styleBackupMap);
}
